import classNames from 'classnames'
import { Fragment, useMemo, useState } from 'react'
import type { ThreatModelContent } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import type { AttackPathTone } from './attack-paths-graph.css.ts'
import * as styles from './attack-paths-graph.css.ts'
import {
  ATTACK_PATH_NODE_RADIUS,
  ATTACK_PATH_NODE_SIZE,
  attackPathLinkKey,
  layOutAttackPaths,
  traceAttackPath,
  type PlacedAttackPathNode,
} from './attack-paths-layout.ts'
import { buildAttackPaths, type AttackPathColumnKey, type AttackPathNode } from './attack-paths.ts'
import { ExposureChip } from './exposure-chip.tsx'
import * as detailStyles from './graph-detail.css.ts'
import { GraphViewport } from './graph-viewport.tsx'

/**
 * The threat model as the path it describes: who can reach the system, through what, which defenses
 * stand in the way, which weaknesses are present, and what is at risk.
 *
 * Hovering a node traces the whole chain through it — forwards and backwards — and dims everything
 * else. Clicking one holds that trace and fills the detail pane from the payload's own prose.
 *
 * **Coverage gaps are marked here** — a count in the header, a badge per weakness, a note in the detail
 * pane. They were Threat coverage's subject until that view was folded into this one, and marking them
 * in both would have made the same fact arrive three times on one screen.
 *
 * Marked in both column modes. In five columns a weakness with no incoming control edge is already
 * visibly undefended — the structural version of the same claim — but the badge appears only where that
 * edge is absent, so the two agree rather than restate each other. In two columns there is no such
 * structural cue, and the badge is the only thing saying it.
 */

/**
 * The columns in reading order, and the noun each contributes to prose describing whatever subset is
 * drawn. Keyed on the column rather than on any single one being present: the header note and the
 * graph's accessible name have to name what is on screen, and keying them on `actor` alone described
 * a two-column view while four columns were showing.
 */
const ATTACK_PATH_COLUMN_KEYS = [
  'actor',
  'entry',
  'control',
  'vuln',
  'asset',
] as const satisfies readonly AttackPathColumnKey[]

const COLUMN_NOUNS: Record<AttackPathColumnKey, string> = {
  actor: 'actors',
  entry: 'entry points',
  control: 'security controls',
  vuln: 'weaknesses',
  asset: 'sensitive assets',
}

/** "security controls and weaknesses", "entry points, security controls and weaknesses". */
const listColumns = (columns: readonly AttackPathColumnKey[]): string => {
  const nouns = ATTACK_PATH_COLUMN_KEYS.filter(key => columns.includes(key)).map(key => COLUMN_NOUNS[key])
  const last = nouns.at(-1)
  return nouns.length > 1 ? `${nouns.slice(0, -1).join(', ')} and ${last}` : (last ?? '')
}

/** Threat coverage's badge geometry, kept when its gap language moved here so the mark is unchanged
 * for anyone who knew it there. */
const GAP_BADGE_WIDTH = 34
const GAP_BADGE_HEIGHT = 15

const TRUST_TONES = ['anonymous', 'authenticated', 'privileged', 'service', 'internal'] as const

const toneFor = (node: AttackPathNode): AttackPathTone => {
  if (node.column === 'actor') {
    return TRUST_TONES.find(level => level === node.tag?.trim().toLowerCase()) ?? 'internal'
  }
  if (node.column === 'vuln') {
    return node.relevance === 'present' ? 'present' : node.relevance === 'likely' ? 'likely' : 'unlikely'
  }
  return node.column === 'entry' ? 'entry' : node.column === 'control' ? 'control' : 'asset'
}

/** Two lines at this box width, then an ellipsis; the full string is always in the detail pane. */
const wrapLabel = (label: string): string[] => {
  const words = label.split(/\s+/)
  const lines: string[] = ['']
  const maxChars = 26
  for (const word of words) {
    const line = lines[lines.length - 1] ?? ''
    if (line.length === 0) {
      lines[lines.length - 1] = word
    } else if (`${line} ${word}`.length <= maxChars) {
      lines[lines.length - 1] = `${line} ${word}`
    } else {
      lines.push(word)
    }
  }
  if (lines.length <= 2) {
    return lines
  }
  const [first, second = ''] = lines
  return [first ?? '', `${second.slice(0, maxChars - 1)}…`]
}

export type AttackPathsGraphProps = {
  content: ThreatModelContent
}

export const AttackPathsGraph: React.FC<AttackPathsGraphProps> = ({ content }) => {
  const [selectedId, setSelectedId] = useState<string>()
  const [hoveredId, setHoveredId] = useState<string>()

  const layout = useMemo(() => layOutAttackPaths(buildAttackPaths({ content })), [content])

  // The drawn column set. A column earns its place by holding nodes *and* connecting to the graph, so
  // this is any subset of the five, not a two-or-five switch: a repository with no sensitive assets
  // recorded drops that column for good, and a document generated before the linking pass drops the
  // outer three until it is regenerated. The legend and the reading-order note follow it so neither
  // describes a column that is not on screen.
  const drawnColumns = useMemo(() => layout.columns.map(column => column.key), [layout])
  const activeColumns = useMemo(() => new Set<AttackPathColumnKey>(drawnColumns), [drawnColumns])
  const tracesFullPath = ATTACK_PATH_COLUMN_KEYS.every(key => activeColumns.has(key))
  // Counted off the drawn nodes rather than the payload, so the number always describes what is on
  // screen — the two-column view drops no vulnerability, but that is a property of the layout, not a
  // guarantee this count should assume.
  const gapCount = useMemo(() => layout.nodes.filter(node => node.isGap).length, [layout])

  const focusedId = hoveredId ?? selectedId
  const traced = useMemo(() => (focusedId ? traceAttackPath(focusedId, layout) : undefined), [focusedId, layout])

  /**
   * Which of the traced weaknesses a control actually defends, and so where the traced path is contested.
   *
   * Read from `defendedBy`, which comes from `security_controls[].mitigates` — the only relationship the
   * payload states. A weakness nobody names stays an open run from actor to asset, which is the
   * structural version of "coverage gap" and needs no badge to say it.
   */
  const contested = useMemo(() => {
    if (!traced) {
      return undefined
    }
    const defendedVulnIds = new Set(
      layout.nodes
        .filter(node => traced.nodeIds.has(node.id) && (node.defendedBy?.length ?? 0) > 0)
        .map(node => node.id)
    )
    /** Curves arriving at a defended weakness: they run into a gate partway. */
    const blocked = layout.links.filter(
      link => traced.linkKeys.has(attackPathLinkKey(link)) && defendedVulnIds.has(link.to)
    )
    return {
      blocked,
      gatedKeys: new Set(blocked.map(attackPathLinkKey)),
      /** Curves leaving one: everything past the gate is conditional on that control failing. */
      beyondKeys: new Set(
        layout.links
          .filter(link => traced.linkKeys.has(attackPathLinkKey(link)) && defendedVulnIds.has(link.from))
          .map(attackPathLinkKey)
      ),
    }
  }, [traced, layout])

  const shown = layout.nodes.find(node => node.id === selectedId)

  return (
    <div className={styles.layout}>
      <div className={styles.canvasMain}>
        <div className={styles.canvasPanel}>
          <div className={styles.header}>
            <div className={styles.headerTags}>
              <ExposureChip internetFacing={content.exposure.internet_facing} />
              {/* No "N of M covered": it is the arithmetic complement of the gap count, so the two said
                  the same thing twice and the gap is the one worth acting on. */}
              <span className={styles.chipAlarm}>
                <span className={styles.chipAlarmValue}>{gapCount}</span> coverage {pluralize(gapCount, 'gap', 'gaps')}
              </span>
              <div className={styles.edgeLegend}>
                <span className={styles.legendItem}>
                  <span className={styles.legendSolid} aria-hidden />
                  Reachable
                </span>
                <span className={styles.legendItem}>
                  <span className={styles.legendGate} aria-hidden />
                  Control blocks here
                </span>
                <span className={styles.legendItem}>
                  <span className={styles.legendGhost} aria-hidden />
                  If the control fails
                </span>
              </div>
            </div>
            <AttackPathsLegend activeColumns={activeColumns} />
            <p className={styles.headerNote}>
              {tracesFullPath
                ? `Reads left to right: who can reach the system, through what, which defenses stand in the way, which weaknesses are present, and what is at risk. Hover a node to trace the chain through it; a bar marks where a control names that weakness, and the dashed run past it is where the attack reaches if the control fails.`
                : `Reads left to right: ${listColumns(drawnColumns)}. Hover a node to trace the chain through it; a bar marks where a control names that weakness, and the dashed run past it is where the attack reaches if the control fails.`}
            </p>
          </div>

          <GraphViewport
            contentSize={layout.contentSize}
            ariaLabel="Attack paths"
            onBackgroundClick={() => setSelectedId(undefined)}
          >
            {scale => (
              <svg
                // `group`, not the default presentational role: an <svg> with no accessible name makes its
                // whole subtree presentational, which drops every `role="button"` node from the
                // accessibility tree while leaving it focusable — the same fix `threat-model-graph.tsx`
                // documents. The GraphViewport's own label covers the surface, not this node's children.
                role="group"
                aria-label={
                  tracesFullPath
                    ? 'Attack paths: actors and entry points through the security controls that stand in the way, to the weaknesses present and the assets they put at risk'
                    : `Attack paths: ${listColumns(drawnColumns)}, connected left to right`
                }
                width={layout.contentSize.width * scale}
                height={layout.contentSize.height * scale}
                viewBox={`0 0 ${layout.contentSize.width} ${layout.contentSize.height}`}
              >
                <g>
                  {layout.columns.map(column => (
                    <g key={column.key}>
                      {/* No inline fontSize: it would override the class, which is what kept these from
                          matching Threat coverage's headings. */}
                      <text className={styles.columnTitle} x={column.x} y={62}>
                        {column.title}
                      </text>
                    </g>
                  ))}
                </g>

                {/* Three layers, in this order, because a line must never appear to cross a card.
                    Untraced edges sit *under* the nodes, whose opaque backing hides them where they pass
                    behind — an entry-to-weakness curve crosses the control column, and at rest that has
                    to read as passing behind those cards, not through them. The traced path then draws
                    *over* the nodes, because following it across the column is the point of tracing, and
                    the gate has to be visible standing in the route. */}
                <g>
                  {layout.links
                    .filter(link => !traced?.linkKeys.has(attackPathLinkKey(link)))
                    .map(link => (
                      <path
                        key={attackPathLinkKey(link)}
                        d={link.d}
                        className={classNames(styles.edge, traced && styles.edgeDimmed)}
                      />
                    ))}
                </g>

                <g>
                  {layout.nodes.map(node => (
                    <NodeBox
                      key={node.id}
                      node={node}
                      isSelected={node.id === selectedId}
                      isDimmed={!!traced && !traced.nodeIds.has(node.id)}
                      onSelect={() => setSelectedId(current => (current === node.id ? undefined : node.id))}
                      onHoverChange={isHovered => setHoveredId(isHovered ? node.id : undefined)}
                    />
                  ))}
                </g>

                <g className={styles.tracedLayer}>
                  {layout.links
                    .filter(link => traced?.linkKeys.has(attackPathLinkKey(link)))
                    .map(link => {
                      const key = attackPathLinkKey(link)

                      // A gated run changes character halfway, so it is two curves meeting on the gate:
                      // solid into it, dashed out of it. Anything else is one curve.
                      if (contested?.gatedKeys.has(key)) {
                        return (
                          <g key={key}>
                            <path d={link.gate.before} className={classNames(styles.edge, styles.edgeTraced)} />
                            <path d={link.gate.after} className={classNames(styles.edge, styles.edgeContingent)} />
                          </g>
                        )
                      }

                      return (
                        <path
                          key={key}
                          d={link.d}
                          className={classNames(
                            styles.edge,
                            contested?.beyondKeys.has(key) ? styles.edgeContingent : styles.edgeTraced
                          )}
                        />
                      )
                    })}
                  {contested?.blocked.map(link => (
                    <rect
                      key={`block-${attackPathLinkKey(link)}`}
                      data-testid="attack-path-block"
                      className={styles.pathBlock}
                      x={link.gate.at.x - 2.5}
                      y={link.gate.at.y - 9}
                      width={5}
                      height={18}
                      rx={2}
                    />
                  ))}
                </g>
              </svg>
            )}
          </GraphViewport>
        </div>
      </div>

      <aside className={styles.detailPane} aria-label="Attack path detail">
        {shown ? <NodeDetail node={shown} /> : <p className={detailStyles.detailEmpty}>Select a node to inspect it.</p>}
      </aside>
    </div>
  )
}

/** Actors carry the trust ramp, vulnerabilities the relevance ramp; the rest is one tone per column. */
const TRUST_LEGEND = [
  { tone: 'anonymous', label: 'Anonymous' },
  { tone: 'authenticated', label: 'Authenticated' },
  { tone: 'privileged', label: 'Privileged' },
  { tone: 'service', label: 'Service' },
  { tone: 'internal', label: 'Internal' },
] as const

/** Between the actor ramp and the relevance ramp, in column order. */
const MIDDLE_LEGEND = [
  { tone: 'entry', label: 'Entry point' },
  { tone: 'control', label: 'Control' },
] as const

const RELEVANCE_LEGEND = [
  { tone: 'present', label: 'Present' },
  { tone: 'likely', label: 'Likely' },
  { tone: 'unlikely', label: 'Unlikely' },
] as const

/** Assets close the row because they close the graph — the key reads in column order. */
const ASSET_LEGEND = [{ tone: 'asset', label: 'Asset' }] as const

type LegendEntry = { tone: AttackPathTone; label: string }

/**
 * The key lists only the swatches for columns that are on screen: the trust ramp and the asset
 * swatch drop out of the two-column view, so nothing names a colour the reader cannot see. Each
 * surviving group is separated from the next, and the groups themselves read in column order.
 */
const AttackPathsLegend: React.FC<{ activeColumns: Set<AttackPathColumnKey> }> = ({ activeColumns }) => {
  const groups: LegendEntry[][] = [
    activeColumns.has('actor') ? [...TRUST_LEGEND] : [],
    MIDDLE_LEGEND.filter(entry => activeColumns.has(entry.tone)),
    activeColumns.has('vuln') ? [...RELEVANCE_LEGEND] : [],
    activeColumns.has('asset') ? [...ASSET_LEGEND] : [],
  ].filter(group => group.length > 0)

  // The gap swatch is not a node tone — it marks a badge, not a fill — so it rides its own trailing
  // group rather than joining the relevance ramp it would otherwise be read as part of.
  const hasGapKey = activeColumns.has('vuln')

  return (
    <div className={styles.legend} aria-label="Colour key">
      {groups.map((group, index) => (
        <Fragment key={group[0]?.tone ?? index}>
          {index > 0 && <span className={styles.legendSeparator} aria-hidden />}
          {group.map(entry => (
            <span key={entry.tone} className={styles.legendItem}>
              <span className={classNames(styles.legendSwatch, styles.legendSwatchColor[entry.tone])} aria-hidden />
              {entry.label}
            </span>
          ))}
        </Fragment>
      ))}
      {hasGapKey && (
        <>
          <span className={styles.legendSeparator} aria-hidden />
          <span className={styles.legendItem}>
            <span className={classNames(styles.legendSwatch, styles.legendSwatchGap)} aria-hidden />
            Coverage gap
          </span>
        </>
      )}
    </div>
  )
}

type NodeBoxProps = {
  node: PlacedAttackPathNode
  isSelected: boolean
  isDimmed: boolean
  onSelect: () => unknown
  onHoverChange: (isHovered: boolean) => unknown
}

const NodeBox: React.FC<NodeBoxProps> = ({ node, isSelected, isDimmed, onSelect, onHoverChange }) => {
  const tone = toneFor(node)
  const lines = wrapLabel(node.label)

  return (
    <g
      className={classNames(isDimmed && styles.nodeDimmed)}
      onPointerEnter={() => onHoverChange(true)}
      onPointerLeave={() => onHoverChange(false)}
    >
      {/* An opaque plate under the tinted fill. The fill is a 16% wash so five saturated columns do not
          fight, which also means any line behind a card would show straight through it. */}
      <rect
        className={styles.nodeBacking}
        x={node.x}
        y={node.y}
        width={ATTACK_PATH_NODE_SIZE.width}
        height={ATTACK_PATH_NODE_SIZE.height}
        rx={ATTACK_PATH_NODE_RADIUS}
      />
      <rect
        className={classNames(
          styles.nodeBox,
          styles.nodeFill[tone],
          styles.nodeStroke[tone],
          isSelected && styles.nodeSelected
        )}
        x={node.x}
        y={node.y}
        width={ATTACK_PATH_NODE_SIZE.width}
        height={ATTACK_PATH_NODE_SIZE.height}
        rx={ATTACK_PATH_NODE_RADIUS}
        tabIndex={0}
        role="button"
        aria-label={node.label}
        aria-pressed={isSelected}
        onClick={onSelect}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onSelect()
          }
        }}
      />
      {lines.map((line, index) => (
        <text key={line} className={styles.nodeLabel} x={node.x + 10} y={node.y + 18 + index * 12} fontSize={10.5}>
          {line}
        </text>
      ))}
      {node.tag && lines.length < 2 && (
        <text className={styles.nodeTag} x={node.x + 10} y={node.y + 38} fontSize={9}>
          {node.tag}
        </text>
      )}
      {/* Straddling the top-right corner rather than sitting on the label's row: it marks the card
          rather than annotating its text, and off the row the label need not shrink for it. */}
      {node.isGap && (
        <>
          <rect
            className={styles.gapBadge}
            x={node.x + ATTACK_PATH_NODE_SIZE.width - GAP_BADGE_WIDTH - 6}
            y={node.y - GAP_BADGE_HEIGHT / 2}
            width={GAP_BADGE_WIDTH}
            height={GAP_BADGE_HEIGHT}
            rx={GAP_BADGE_HEIGHT / 2}
          />
          <text
            className={styles.gapBadgeText}
            x={node.x + ATTACK_PATH_NODE_SIZE.width - GAP_BADGE_WIDTH / 2 - 6}
            y={node.y + 4}
            textAnchor="middle"
          >
            GAP
          </text>
        </>
      )}
    </g>
  )
}

const NodeDetail: React.FC<{ node: AttackPathNode }> = ({ node }) => (
  <>
    <p className={detailStyles.eyebrow}>{COLUMN_EYEBROWS[node.column]}</p>
    <h4 className={detailStyles.detailTitle}>{node.label}</h4>
    {node.tag && <span className={detailStyles.tag}>{node.tag}</span>}
    {node.fields.map(field => (
      <div key={field.key} className={detailStyles.field}>
        <p className={detailStyles.fieldKey}>{field.key}</p>
        <p className={detailStyles.fieldValue}>{field.value}</p>
      </div>
    ))}
    {node.location && (
      <div className={detailStyles.field}>
        <p className={detailStyles.fieldKey}>Location</p>
        <p className={detailStyles.fieldLocation}>{node.location}</p>
      </div>
    )}
    {node.isGap && (
      <div className={styles.gapNote}>
        <span aria-hidden>⚠</span>
        <span>
          <span className={styles.gapNoteStrong}>Coverage gap. </span>
          No control&rsquo;s mitigates list names this weakness.
        </span>
      </div>
    )}
  </>
)

/** The singular of each column heading, so the pane names the same thing the column does. */
const COLUMN_EYEBROWS = {
  actor: 'Actor',
  entry: 'Entry point',
  control: 'Security control',
  vuln: 'Vulnerability',
  asset: 'Sensitive asset',
} as const
